import { nonEmptyString } from './format';
import type { Claim, Element, Evidence, Fact, FactLink, Party, ProofGraph } from '../types';

// Normalizes party types so that the legacy backend value "other" (used before
// the frontend/backend agreed on 'tp') is converted to the canonical 'tp'.
function normalizePartyTypes(graph: ProofGraph): ProofGraph {
  if (!Array.isArray(graph.parties)) return graph;
  const VALID_TYPES = new Set<Party['type']>(['us', 'them', 'tp']);
  const needsFix = graph.parties.some(p => !VALID_TYPES.has(p.type));
  if (!needsFix) return graph;
  return {
    ...graph,
    parties: graph.parties.map(p =>
      VALID_TYPES.has(p.type) ? p : { ...p, type: 'tp' as const },
    ),
  };
}
import { relationshipTouchesFact } from './factRelationships';

export function genPoolId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
}

// Migrates a legacy proof graph (facts nested under a single element,
// evidence nested under a single fact, unmapped_facts as a sibling array)
// into the current flat, shared-pool shape: proofGraph.facts and
// proofGraph.evidence are the single source of truth, and elements/facts
// reference them by id. A no-op once a graph already has `facts`/`evidence`
// top-level arrays.
type LegacyFact = Fact & { evidence?: Partial<Evidence>[]; valence?: FactLink['valence'] };
type LegacyElement = Element & { facts?: LegacyFact[] | undefined };
type LegacyGraph = ProofGraph & { claims: (Claim & { elements: LegacyElement[] })[]; unmapped_facts?: LegacyFact[] };
export function migrateProofGraph(input: null): null;
export function migrateProofGraph(input: undefined): undefined;
export function migrateProofGraph(input: object): ProofGraph;
export function migrateProofGraph(input: object | null | undefined): ProofGraph | null | undefined;
export function migrateProofGraph(input: object | null | undefined): ProofGraph | null | undefined {
  const raw = input as LegacyGraph | null | undefined;
  if (!raw || (Array.isArray(raw.facts) && Array.isArray(raw.evidence))) {
    return raw ? normalizePartyTypes(raw) : raw;
  }

  const evidencePool: Evidence[] = [];
  const evidenceIdByClioDocId = new Map<string, string>();
  const factsPool: Fact[] = [];

  const resolveEvidence = (evRef: Partial<Evidence>): string => {
    const clioDocId = (evRef?.clio_doc_id ?? '');
    const existingId = clioDocId ? evidenceIdByClioDocId.get(clioDocId) : undefined;
    if (existingId) return existingId;
    const newEvidence: Evidence = {
      id: genPoolId('ev'),
      clio_doc_id: clioDocId,
      label: (evRef?.label ?? ''),
      deeplink: (evRef?.deeplink ?? ''),
    };
    evidencePool.push(newEvidence);
    if (clioDocId) evidenceIdByClioDocId.set(clioDocId, newEvidence.id);
    return newEvidence.id;
  };

  const poolFactAndLink = (legacyFact: LegacyFact): FactLink => {
    const evidenceIds = ((legacyFact.evidence ?? [])).map(resolveEvidence);
    if (!factsPool.some(f => f.id === legacyFact.id)) {
      factsPool.push({
        id: legacyFact.id,
        text: legacyFact.text,
        status: legacyFact.status,
        evidence_ids: evidenceIds,
      });
    }
    return { fact_id: legacyFact.id, valence: (nonEmptyString(legacyFact.valence) ?? 'Supports') };
  };

  const claims = (raw.claims || []).map((claim: Claim & { elements: LegacyElement[] }) => ({
    ...claim,
    elements: (claim.elements || []).map((element: LegacyElement) => ({
      ...element,
      fact_links: ((element.facts ?? [])).map(poolFactAndLink),
      facts: undefined,
    })),
  }));

  ((raw.unmapped_facts ?? [])).forEach((legacyFact) => {
    if (!factsPool.some(f => f.id === legacyFact.id)) {
      const evidenceIds = ((legacyFact.evidence ?? [])).map(resolveEvidence);
      factsPool.push({
        id: legacyFact.id,
        text: legacyFact.text,
        status: legacyFact.status,
        evidence_ids: evidenceIds,
      });
    }
  });

  const migrated = { ...raw, claims, facts: factsPool, evidence: evidencePool };
  delete migrated.unmapped_facts;
  migrated.motions ??= [];
  migrated.persons ??= [];
  migrated.places ??= [];
  return normalizePartyTypes(migrated);
}

/**
 * Pure helper to remove a fact from a ProofGraph:
 * - Removes the fact from graph.facts
 * - Removes all fact_links to this fact across all claims and elements
 * - Removes all fact_relationships involving this fact
 */
export function deleteFactFromGraph(graph: ProofGraph, factId: string): ProofGraph {
  const facts = ((graph.facts ?? [])).filter(f => f.id !== factId);
  const claims = ((graph.claims ?? [])).map(claim => ({
    ...claim,
    elements: ((claim.elements ?? [])).map(element => ({
      ...element,
      fact_links: ((element.fact_links ?? [])).filter(link => link.fact_id !== factId),
    })),
  }));
  const fact_relationships = (graph.fact_relationships ?? []).filter(
    relationship => !relationshipTouchesFact(relationship, factId)
  );

  return {
    ...graph,
    facts,
    claims,
    fact_relationships,
  };
}
